import { Alert, capitalize } from "@mui/material";
import { isAxiosError } from "axios";
import { isObject, omit } from "lodash-es";
import React from "react";
import { BiSolidError } from "react-icons/bi";
import { isAppError, isErrorWithUserMessage, isSerializedModelWithErrors } from "../types/error";
import { getLcn } from "~/utils/labeled-classnames";

type DisplayableErrorContent = string | string[] | { [key: string]: string }
type ErrorContentObject = { [key: string]: DisplayableErrorContent }

const iterateErrorObject = (errorHash: ErrorContentObject) => {
  return Object.entries(errorHash).map(([key, value]) => {

    // no use showing the error keys themselves
    const isErrorKey = key === 'error' || key === 'errors';
    const printableKey = isErrorKey ? '' : `${capitalize(key)}:`;
    const className = isErrorKey ? '' : 'pl-2';

    return <div key={key} className={className}>
      {printableKey} <ErrorRow value={value} />
    </div>;
  });
}

const ErrorRow = ({ value }: { value: DisplayableErrorContent }) => {
  let renderableValue: string | React.ReactNode

  if (Array.isArray(value)) {
    renderableValue = capitalize(value.join(', '));
  } else if (typeof value === 'object' && value !== null) {
    return iterateErrorObject(value);
  } else if (typeof value === 'string') {
    renderableValue = capitalize(value);
  } else if (typeof value === 'undefined') {
    renderableValue = ''
  } else {
    // boolean, null, undefined, or other types 
    renderableValue = typeof value;
  }

  return <div className='pl-3'>{renderableValue} </div>
}


// errors come in all shapes and sizes
// this tries to figure what kind of error it is and show it appropriately
export const RenderError = ({
  error,
  excludeKeys = [],
  useBox,
  unformatted = false
}: {
  error: unknown,
  excludeKeys?: string[],
  useBox?: boolean
  unformatted?: boolean
}) => {
  const defaultExcludeKeys = ['status', 'meta', 'error_class']
  const allExcludeKeys = [...defaultExcludeKeys, ...excludeKeys]

  const lcn = getLcn({
    errorDisplay: 'text-left text-red-500',
  });

  const renderedError = (error: unknown) => {
    let errorData;

    if (isAxiosError(error)) {
      if (isErrorWithUserMessage(error.response?.data)) {
        errorData = error.response.data.error.detail
      } else if (isErrorWithUserMessage(error.response?.data)) {
        errorData = error.response.data.error.detail
      } else if (isAppError(error.response?.data)) {
        errorData = error.response.data.error.detail
      } else if (isSerializedModelWithErrors(error.response?.data)) {
        errorData = error.response.data.errors;
      } else if (error.response?.data) {
        // random data that comes back from server with 500 or 400 errors
        // we should avoid this type of data
        errorData = error.response.data;
      } else {
        // errors that didn't involve the server sending pre-determined data
        errorData = error.message
      }
    } else {
      errorData = error
    }

    if (isObject(errorData)) {
      errorData = omit(errorData, excludeKeys);
      return iterateErrorObject(errorData as ErrorContentObject);
    } else {
      return <ErrorRow value={errorData as string} />;
    }
  }

  const unformattedError = renderedError(error);
  const errorDisplay = <div className={lcn.errorDisplay}>{unformattedError}</div>

  if (unformatted) {
    return unformattedError
  } else if (useBox) {
    return <Alert icon={<BiSolidError fontSize="inherit" />} severity="error" >
      {errorDisplay}
    </Alert>
  } else {
    return errorDisplay
  }
}